import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, children, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-popover',
      'rounded-sm bg-ink text-cream px-2 py-1',
      'text-[0.75rem] leading-4 font-medium',
      'shadow-md',
      'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
      'data-[side=bottom]:slide-in-from-top-1',
      'data-[side=left]:slide-in-from-right-1',
      'data-[side=right]:slide-in-from-left-1',
      'data-[side=top]:slide-in-from-bottom-1',
      className,
    )}
    {...props}
  >
    {children}
    <TooltipPrimitive.Arrow className="fill-ink" width={10} height={5} />
  </TooltipPrimitive.Content>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps {
  /** Tooltip body. Required. */
  content: React.ReactNode;
  /** Trigger element. Must be a single React element. */
  children: React.ReactElement;
  /** Side relative to the trigger. */
  side?: TooltipSide;
  /** Alignment along the chosen side. */
  align?: 'start' | 'center' | 'end';
  /** Delay before the tooltip shows on hover. 0 for action confirmations. */
  delay?: number;
  /** Inline keyboard shortcut hint, e.g. "⌘K". Rendered as a `<kbd>`. */
  shortcut?: string;
  /** Controlled open state. */
  open?: boolean;
  /** Controlled open-state change handler. */
  onOpenChange?: (open: boolean) => void;
}

const Tooltip = ({
  content,
  children,
  side = 'top',
  align = 'center',
  delay = 200,
  shortcut,
  open,
  onOpenChange,
}: TooltipProps) => (
  <TooltipRoot delayDuration={delay} open={open} onOpenChange={onOpenChange}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipPortal>
      <TooltipContent side={side} align={align}>
        <span className="inline-flex items-center gap-2">
          {content}
          {shortcut && (
            <kbd className="rounded-sm border border-cream/30 px-1 py-0.5 text-[0.6875rem] font-mono">
              {shortcut}
            </kbd>
          )}
        </span>
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
);
Tooltip.displayName = 'Tooltip';

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
  TooltipPortal,
};
